import pino, { type Logger as PinoLogger } from 'pino';

import { rg4js } from './raygun';
import { formatError } from './formatError';
import { type LogLevel, type LogObject } from './logger.types';

const isDev = process.env['NODE_ENV'] !== 'production';
const logLevel = process.env['LOG_LEVEL'] || (isDev ? 'info' : 'warn');

const serializeError = (value: unknown) => {
  // Sometimes a string will be passed in as the err or error field to pino. When this happens, we
  // want to ensure the message is converted into an object so fluent-bit can properly handle it.
  if (typeof value === 'string') {
    return {
      message: value,
    };
  } else if (value instanceof Error) {
    return pino.stdSerializers.err(value);
  } else {
    return value;
  }
};

// Since pino doesn't write to the console directly as part of its normal operation, we need to mock
// it in test.
const isTest =
  process.env['JEST_WORKER_ID'] !== undefined ||
  process.env['VITEST'] !== undefined;

const createPinoInstance = (): PinoLogger => {
  return pino(
    {
      level: logLevel,
      // In production, Next.js logs will be picked up by fluent-bit and sent to Kibana.
      transport: isDev
        ? // For local dev, use a pretty-printer for readability
          { target: 'pino-pretty' }
        : undefined,
      // This manages how various keys of a logged object are serialized when they are ready to be
      // sent off.
      serializers: {
        err: serializeError,
        // Without this, errors may come through as just {}.
        error: serializeError,
      },
      // Pino's default message key is "msg". We want to use "message" instead to align w/ the rest
      // of our observability stack.
      messageKey: 'message',
      formatters: {
        // Use labels instead of numbers for the level field. By default, pino will use levels like
        // 30 for info, 40 for warn, and 50 for error. We prefer the strings.
        level: (label) => {
          return {
            level: label,
          };
        },
      },
      redact: {
        paths: [
          // We don't want Bearer tokens to be logged when an AxiosError is logged.
          'error.config.headers.Authorization',
        ],
      },
      // If the code is running in the browser, we will forward logs to a custom transport that sends
      // them to Raygun.
      browser: {
        transmit: {
          // Send everything at or above the info level.
          level: logLevel,
          send: (_level, { messages }) => {
            // In the Logger signature, we've ensured the only type of message that gets to the Logger
            // is a single LogObject.
            const [logObject] = messages as [LogObject];
            const { error, tags, ...customData } = logObject;

            rg4js('send', {
              error: error as Error,
              tags,
              customData,
            });
          },
        },
      },
    },
    // Only in test, we'll inject a custom destination that writes to the console. This is required
    // to test pino's output.
    isTest
      ? {
          write: (logObject) => {
            // eslint-disable-next-line no-console
            console.log(logObject);
          },
        }
      : undefined,
  );
};

const getSingletonPinoInstance = (() => {
  let instance: PinoLogger | null = null;

  return (): PinoLogger => {
    if (instance) {
      return instance;
    }

    instance = createPinoInstance();
    return instance;
  };
})();

const createLoggerForLevel =
  (level: LogLevel) =>
  (logObject: LogObject): void => {
    const pinoInstance = getSingletonPinoInstance();

    // Only errors need special formatting. Generally we use log and warn for simple messages and
    // data. It's only errors which will be varied and benefit from the formatting.
    if (level === 'error') {
      const formatted = formatError(level, logObject.message, logObject.error);
      pinoInstance[level]({
        ...logObject,
        ...formatted,
      });
    } else {
      pinoInstance[level](logObject);
    }
  };

/**
 * A logger backed by Pino which can be used to log anywhere in the app. It will automatically
 * forward logs to the right place depending on the environment.
 */
export const logger: Record<LogLevel, (logObject: LogObject) => void> = {
  info: createLoggerForLevel('info'),
  warn: createLoggerForLevel('warn'),
  error: createLoggerForLevel('error'),
};

export type Logger = typeof logger;
